import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link, useLocation } from "wouter";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { useQuery } from "@tanstack/react-query";
import { EditablePlanName } from "@/components/editable-plan-name";

interface SavedPlan {
  id: number;
  userId: string;
  name?: string;
  planData: any;
  createdAt: string;
}

export default function MyPlansPage() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Query to fetch user's saved plans
  const { data: userPlans, isLoading: plansLoading } = useQuery({
    queryKey: ['/api/plans/user', user?.id],
    enabled: !!user?.id,
  });

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!isLoading && !isAuthenticated) {
      setLocation('/login');
      return;
    }
  }, [isAuthenticated, isLoading, setLocation]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:bg-gradient-to-br dark:from-[var(--dark-1)] dark:to-[var(--dark-2)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated (redirect is handled in useEffect)
  if (!isAuthenticated) {
    return null;
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const sortedPlans = userPlans && Array.isArray(userPlans) 
    ? [...userPlans].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    : [];

  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:bg-gradient-to-br dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">My Training Plans</h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg">
              View and manage your saved training plans
            </p>
          </div>

          {plansLoading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">Loading your plans...</p>
            </div>
          ) : sortedPlans.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-200 dark:bg-[var(--dark-3)] rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold mb-4">No Plans Saved Yet</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Create your first personalized training plan to get started.
              </p>
              <Link href="/form">
                <Button className="btn-primary px-8 py-3 rounded-2xl">
                  Create Your First Plan
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {sortedPlans.map((plan: SavedPlan, index: number) => (
                <Card
                  key={plan.id}
                  className="glass-card dark:glass-card-dark rounded-3xl shadow-lg card-hover animate-scale-in overflow-hidden group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 min-w-0 flex-1">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-500 dark:from-gray-300 dark:to-gray-600 flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-lg">{index + 1}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <EditablePlanName
                            planId={plan.id}
                            currentName={plan.name || ''}
                            planNumber={index + 1}
                          />
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>Saved {formatDate(plan.createdAt)}</span>
                            </div>
                            {plan.planData?.days && (
                              <div className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4" />
                                <span>{plan.planData.days.length} days</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <Link href={`/plan/${plan.id}`}>
                        <Button 
                          variant="outline"
                          className="px-6 py-2 rounded-2xl border-2 border-gray-300 text-white hover:bg-white/10 transition-all duration-200 ml-4"
                        >
                          View Plan
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                    
                    {plan.planData?.overview && (
                      <div className="mt-4 p-4 bg-gray-50 dark:bg-[var(--dark-3)] rounded-2xl">
                        <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                          {plan.planData.overview}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="text-center mt-12">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/form">
                <Button 
                  variant="outline"
                  className="px-8 py-3 rounded-2xl border-2 border-gray-300 text-white font-semibold hover:bg-white/10 transition-all"
                >
                  Create New Plan
                </Button>
              </Link>
              <Link href="/">
                <Button
                  variant="outline"
                  className="px-8 py-3 rounded-2xl border-2 border-gray-300 text-white font-semibold hover:bg-white/10 transition-all"
                >
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}